import { FC, useEffect, useMemo } from 'react';
import moment from 'moment';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
	Box,
	Paper,
	Grid,
	Button,
	TextField,
	CircularProgress,
	Autocomplete,
	Typography,
} from '@mui/material';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';

import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import { useToastContext } from 'core/components/Toast/context/ToastContext';

import { PostShifts, EditShifts, fetchAllShifts } from 'core/API/shifts';
import { getJobCodes, getPositions } from 'core/API/scheduler';
import { IShiftPayload } from 'module/schedule/interfaces/ShiftsInterface';
import {
	collectShiftMetricDates,
	syncSchedulerMetricsForDates,
} from 'module/schedule/utils/schedulerMetricsUtils';
import {
	getOvertimeThresholds,
	hasEmployeePickerOvertimeWarning,
} from 'module/schedule/utils/scheduleEmployeeStatus';
import {
	buildAllWeekDays,
	getLatestAvailabilityForDay,
} from 'module/schedule/utils/availabilityDisplay';

import {
	filterPositionsByJobCode,
	NONE_POSITION_OPTION,
	SCHEDULER_POSITIONS_QUERY_KEY,
} from 'module/schedule/utils/scheduleDepartmentUtils';
import { getShiftLengthValidationError } from 'module/schedule/utils/shiftValidation';
import {
	getShiftFormDateLimits,
	toLocalDateTimeValue,
} from 'module/schedule/utils/shiftFormDateLimits';
import {
	findEmployeeOption,
	toUserIri,
	withAssignedEmployeeOption,
} from 'module/schedule/utils/shiftEmployeeOptions';

import { ICellClick } from 'module/schedule/pages/SchedulePage';

import unavailableIcon from 'assets/images/unavailable.png';
import minorIcon from 'assets/images/minor.png';
import overtimeIcon from 'assets/images/overtime.png';

// import ConfirmDialog from '../../../schedule/Modals/ConfirmDialog/ConfirmDialog';
import styles from 'module/events/pages/CreateEvent.module.scss';

import './AddShiftPage.scss';
import { Days } from '../../../../types';

// Extended interface to include job code and employee from resource clicks
export interface IExtendedCellClick extends ICellClick {
	jobCodeFromResource?: {
		id: string;
		'@id': string;
		label: string;
		jobNumber?: string;
		backgroundColor?: string;
		textColor?: string;
	};
	employeeFromResource?: {
		id: string;
		'@id': string;
		firstName: string;
		lastName: string;
		fullName: string;
	};
}

// Validation schema for the shift form
const getValidationSchema = (minShiftLength?: number, maxShiftLength?: number) => {
	const minHours = minShiftLength || 2;
	const maxHours = maxShiftLength || 16;

	return Yup.object({
		startAt: Yup.string().required('Start date and time are required'),
		endAt: Yup.string()
			.required('End date and time are required')
			.test(
				'end-after-start',
				'End date and time must be after start date and time',
				function (value) {
					const { startAt } = this.parent;
					if (!startAt || !value) return true; // Let required validation handle empty values

					const startMoment = moment(startAt);
					const endMoment = moment(value);

					return endMoment.isAfter(startMoment);
				}
			)
			.test('shift-length', function (value) {
				const { startAt } = this.parent;
				if (!startAt || !value) return true;

				const error = getShiftLengthValidationError(startAt, value, {
					minShiftLength: minHours,
					maxShiftLength: maxHours,
				});
				if (!error) return true;

				return this.createError({ message: error });
			}),
		jobCode: Yup.string().nullable().required('Job code is required'),
		position: Yup.string().nullable(),
		// employee is optional (null allowed for open shifts)
		employee: Yup.string().nullable(),
		workplace: Yup.string().required('Workplace is required'),
	});
};

// Use the interface from ShiftsInterface for consistency
type IShiftForm = IShiftPayload;

interface IAddShiftPage {
	onBack: () => void;
	editShift?: any;
	addNewShift?: IExtendedCellClick;
	generalSettings?: {
		defaultShiftLengthHours: number;
		minShiftLength?: number;
		maxShiftLength?: number;
		ageOfMinor?: number;
		overtimeDailyHoursAt?: number;
		overtimeWeeklyHoursAt?: number;
	} | null;
	workplaceUsers?: any[]; // Users data from SchedulePage for reference
	daysData?: Days[]; // Days data from SchedulePage for availability checking
	weekStartDate?: string; // Start of the selected business week
	workplaceWorkingHoursStart?: string; // Workplace working hours start time (HH:mm format)
}

const AddShiftPage: FC<IAddShiftPage> = ({
	onBack,
	editShift,
	addNewShift,
	generalSettings,
	workplaceUsers: workplaceUsersFromParent,
	daysData,
	weekStartDate,
	workplaceWorkingHoursStart,
}) => {
	const { workplaceId, timezones: _timezones } = useWorkplacesContext();
	const { showToast } = useToastContext();
	const queryClient = useQueryClient();

	// Ensure Back/Save header is visible when opening from a scrolled calendar
	useEffect(() => {
		window.scrollTo(0, 0);
		document.querySelector('main')?.scrollTo(0, 0);
	}, []);

	// const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

	// Use workplace users from parent (already filtered by SchedulePage)
	const employees = useMemo(() => workplaceUsersFromParent || [], [workplaceUsersFromParent]);

	// console.log('AddShiftPage workplaceUsersFromParent:', workplaceUsersFromParent);
	// console.log('daysData:', daysData);
	const weekFromDate = weekStartDate
		? moment(weekStartDate).format('YYYY-MM-DD')
		: undefined;
	const weekToDate = weekStartDate
		? moment(weekStartDate).add(6, 'days').format('YYYY-MM-DD')
		: undefined;

	const { data: weekShiftsData } = useQuery(
		['shifts', workplaceId, weekFromDate, weekToDate],
		() =>
			fetchAllShifts({
				workplaceId,
				fromDate: weekFromDate!,
				toDate: weekToDate!,
			}),
		{
			enabled: !!workplaceId && !!weekFromDate && !!weekToDate,
			staleTime: 30000,
		}
	);

	// Helper function to calculate employee status
	const getEmployeeStatus = (employee: any, shiftStartTime?: Date, shiftEndTime?: Date) => {
		const statuses = {
			isUnavailable: false,
			isMinor: false,
			hasOvertime: false,
			availabilityMessage: null as string | null,
		};

		if (!employee || !generalSettings) return statuses;

		// Check if employee is a minor
		const { minorAge } = getOvertimeThresholds(generalSettings);
		if (employee.dateOfBirth) {
			const age = moment().diff(moment(employee.dateOfBirth), 'years');
			statuses.isMinor = age < minorAge;
		}

		// Live OT warning: approaching uses existing hours only so a default-length
		// draft shift does not flag every employee. Actual OT still includes the draft.
		if (weekFromDate && weekToDate && employee.id && employee.id !== 'HOUSE_SHIFT') {
			const currentShiftId = editShift?.shift_id || editShift?.id || 'draft';
			const existingShifts = (weekShiftsData?.member || []).filter(
				(shift: any) =>
					String(shift.employee?.id) === String(employee.id) &&
					String(shift.id) !== String(currentShiftId) &&
					String(shift.shift_id) !== String(currentShiftId)
			);
			const draftShift =
				shiftStartTime && shiftEndTime
					? {
							id: currentShiftId,
							employee: { id: employee.id, '@id': employee['@id'] },
							startAt: moment(shiftStartTime).format('YYYY-MM-DDTHH:mm:ss'),
							endAt: moment(shiftEndTime).format('YYYY-MM-DDTHH:mm:ss'),
						}
					: null;

			statuses.hasOvertime = hasEmployeePickerOvertimeWarning({
				employeeId: employee.id,
				existingShifts,
				draftShift,
				fromDate: weekFromDate,
				toDate: weekToDate,
				generalSettings,
			});
		}

		// Check for availability conflicts or time off
		if (shiftStartTime && shiftEndTime) {
			const shiftMoment = moment(shiftStartTime);
			const shiftDayOfWeek = shiftMoment.day(); // 0 = Sunday, 1 = Monday, etc.
			const shiftStartTimeOnly = shiftMoment.format('HH:mm:ss');
			const shiftEndTimeOnly = moment(shiftEndTime).format('HH:mm:ss');
			const dayNames = [
				'Sunday',
				'Monday',
				'Tuesday',
				'Wednesday',
				'Thursday',
				'Friday',
				'Saturday',
			];
			const shiftDayName = dayNames[shiftDayOfWeek];

			// Check userAvailability (recurring weekly pattern)
			if (daysData) {
				// Check if employee has ANY availability set for any day
				// If they have some availability set, then days without availability are considered unavailable
				// If they have NO availability set at all, they are available all days
				const hasAnyAvailability =
					employee.userAvailability && employee.userAvailability.length > 0;

				if (hasAnyAvailability) {
					const weekDays = buildAllWeekDays(daysData);
					const shiftDay = weekDays.find((day) => day.label === shiftDayName);
					const latestAvailability = shiftDay
						? getLatestAvailabilityForDay(employee.userAvailability, shiftDay)
						: null;

					if (latestAvailability && latestAvailability.startAt && latestAvailability.endAt) {
						const availStartTimeOnly = moment(latestAvailability.startAt).format('HH:mm:ss');
						const availEndTimeOnly = moment(latestAvailability.endAt).format('HH:mm:ss');

						if (availStartTimeOnly === availEndTimeOnly) {
							statuses.isUnavailable = true;
							statuses.availabilityMessage = `Not available on ${shiftDayName}`;
						} else {
							const isOvernightAvailability = availEndTimeOnly < availStartTimeOnly;
							let isWithin: boolean;

							if (isOvernightAvailability) {
								const shiftStartInWindow =
									shiftStartTimeOnly >= availStartTimeOnly ||
									shiftStartTimeOnly <= availEndTimeOnly;
								const shiftEndInWindow =
									shiftEndTimeOnly >= availStartTimeOnly ||
									shiftEndTimeOnly <= availEndTimeOnly;
								isWithin = shiftStartInWindow && shiftEndInWindow;
							} else {
								isWithin =
									shiftStartTimeOnly >= availStartTimeOnly &&
									shiftEndTimeOnly <= availEndTimeOnly;
							}

							if (!isWithin) {
								statuses.isUnavailable = true;
								statuses.availabilityMessage = `Available on ${shiftDayName}: ${moment(latestAvailability.startAt).format('h:mm A')} - ${moment(latestAvailability.endAt).format('h:mm A')}`;
							}
						}
					} else {
						statuses.isUnavailable = true;
						statuses.availabilityMessage = `Not available on ${shiftDayName}`;
					}
				}
				// If hasAnyAvailability is false, employee has no availability set at all,
				// so they are available all days (no issue)
			}
			// console.log('Employee userTimeOff:', employee.userTimeOff);

			// Check userTimeOff (specific date ranges)
			if (employee.userTimeOff && employee.userTimeOff.length > 0) {
				// Extract only the date part of the shift (ignore time)
				const shiftDate = shiftMoment.format('YYYY-MM-DD');

				const matchingTimeOff = employee.userTimeOff.find((timeOff: any) => {
					if (timeOff.startAt && timeOff.endAt) {
						// Extract only date parts for comparison (ignore time)
						const timeOffStartDate = moment(timeOff.startAt).format('YYYY-MM-DD');
						const timeOffEndDate = moment(timeOff.endAt).format('YYYY-MM-DD');
						console.log('shiftDate vs timeOff:', shiftDate, timeOffStartDate, timeOffEndDate);
						// Check if shift date falls within time-off period (inclusive)
						return shiftDate >= timeOffStartDate && shiftDate <= timeOffEndDate;
					}
					return false;
				});

				if (matchingTimeOff) {
					statuses.isUnavailable = true;
					// Format time-off dates for display
					const timeOffStart = moment(matchingTimeOff.startAt).format('MMM D, YYYY');
					const timeOffEnd = moment(matchingTimeOff.endAt).format('MMM D, YYYY');
					statuses.availabilityMessage =
						timeOffStart === timeOffEnd
							? `Employee has time off on ${timeOffStart}`
							: `Employee has time off: ${timeOffStart} - ${timeOffEnd}`;
				}
			}
		}

		return statuses;
	};

	// Fetch job codes
	const { data: jobCodes, isLoading: isLoadingJobCodes } = useQuery(
		['jobCodes'],
		() => getJobCodes(),
		{
			staleTime: 300000,
		}
	);

	const { data: positionsData } = useQuery(SCHEDULER_POSITIONS_QUERY_KEY, () => getPositions(), {
		staleTime: 0,
		refetchOnMount: true,
		retry: false,
	});

	const positions = positionsData || [];

	// Sort job codes by job number
	const sortedJobCodes = useMemo(() => {
		if (!jobCodes) return [];
		return [...jobCodes].sort((a, b) => {
			const aNum = a.jobNumber ?? 999;
			const bNum = b.jobNumber ?? 999;
			return aNum - bNum;
		});
	}, [jobCodes]);

	// Sort employees by name
	const sortedEmployees = useMemo(() => {
		if (!employees) return [];
		return [...employees].sort((a, b) => {
			const aName = `${a.firstName} ${a.lastName}`;
			const bName = `${b.firstName} ${b.lastName}`;
			return aName.localeCompare(bName);
		});
	}, [employees]);

	// Use editShift data directly instead of fetching from API
	// No need for separate fetchedShift variable since we use editShift directly
	// timeSlots/baseDates not required in this version (kept for future enhancements)
	const syncMetricsForShiftDates = async (dates: Array<string | Date | null | undefined>) => {
		if (!workplaceId || !weekStartDate) return;

		const fromDate = moment(weekStartDate).format('YYYY-MM-DD');
		const toDate = moment(weekStartDate).add(6, 'days').format('YYYY-MM-DD');
		const freshData = await queryClient.fetchQuery(
			['shifts', workplaceId, fromDate, toDate],
			() =>
				fetchAllShifts({
					workplaceId,
					fromDate,
					toDate,
				})
		);

		await syncSchedulerMetricsForDates({
			workplaceId,
			dates,
			shifts: freshData?.member || [],
			dailyOTThreshold: generalSettings?.overtimeDailyHoursAt ?? 8,
		});

		queryClient.invalidateQueries(['scheduler-metrics', workplaceId]);
	};

	const { mutate: addShift, isLoading: isAddLoading } = useMutation(PostShifts, {
		onSuccess: async (_data, variables) => {
			queryClient.invalidateQueries(['shifts']);
			await syncMetricsForShiftDates([variables.startAt]);
			onBack();
		},
		onError: (error: any) => {
			showToast('error', error?.message || 'Failed to add shift');
		},
	});

	const { mutate: updateShift, isLoading: isUpdateLoading } = useMutation(
		(params: { id: number; body: IShiftPayload; metricDates?: Array<string | Date | null | undefined> }) =>
			EditShifts(params.id, params.body),
		{
			onSuccess: async (_data, variables) => {
				queryClient.invalidateQueries(['shifts']);
				await syncMetricsForShiftDates(
					variables.metricDates || [variables.body.startAt]
				);
				onBack();
			},
			onError: (error: any) => {
				showToast('error', error?.message || 'Failed to update shift');
			},
		}
	);

	const initialValues = useMemo(() => {
		// Store dates in local timezone for UI display
		// For current week, default to today's date; for past/future weeks, use week start
		let defaultStartDate: Date;
		if (weekStartDate) {
			const today = moment().startOf('day');
			const weekStart = moment(weekStartDate).startOf('day');
			const weekEnd = weekStart.clone().add(6, 'days').endOf('day');

			// Check if today falls within the week range
			const isCurrentWeek = today.isBetween(weekStart, weekEnd, null, '[]');

			if (isCurrentWeek) {
				// Current week: use today's date
				defaultStartDate = moment().startOf('day').toDate();
			} else {
				// Past or future week: use week start
				defaultStartDate = moment(weekStartDate).startOf('day').toDate();
			}
		} else {
			defaultStartDate = moment().startOf('day').toDate();
		}

		// Apply workplace working hours start time to the default start date
		if (workplaceWorkingHoursStart && !editShift && !addNewShift?.start_at) {
			const [hours, minutes] = workplaceWorkingHoursStart.split(':').map(Number);
			defaultStartDate = moment(defaultStartDate)
				.set({ hour: hours, minute: minutes, second: 0, millisecond: 0 })
				.toDate();
		}

		const startTime = editShift?.start || addNewShift?.start_at || defaultStartDate;
		const defaultHours = generalSettings?.defaultShiftLengthHours || 8;
		let endTime: Date;
		if (editShift?.end) {
			endTime = new Date(editShift.end);
		} else if (addNewShift?.end_at) {
			endTime = new Date(addNewShift.end_at);
		} else {
			// For new shifts, calculate end time using default shift length
			endTime = moment(startTime).add(defaultHours, 'hours').toDate();
		}

		return {
			workplace: `api/workplaces/${workplaceId}`,
			startAt: toLocalDateTimeValue(startTime),
			endAt: toLocalDateTimeValue(endTime),
			employee: (() => {
				if (editShift?.employee) {
					return toUserIri(editShift.employee);
				}
				if (addNewShift?.employeeFromResource) {
					return toUserIri(addNewShift.employeeFromResource);
				}
				return null;
			})(),
			jobCode: (() => {
				// Priority: editShift jobCode, then jobCode from resource click, then null
				if (editShift?.jobCode && editShift.jobCode['@id']) {
					return editShift.jobCode['@id'];
				}
				if (addNewShift?.jobCodeFromResource && addNewShift.jobCodeFromResource['@id']) {
					return addNewShift.jobCodeFromResource['@id'];
				}
				return null;
			})(),
			position: (() => {
				if (editShift?.position) {
					if (typeof editShift.position === 'string') return editShift.position;
					return editShift.position['@id'] || null;
				}
				return null;
			})(),
		};
	}, [
		workplaceId,
		editShift,
		addNewShift,
		generalSettings?.defaultShiftLengthHours,
		weekStartDate,
	]);

	const { values, errors, touched, submitForm, setFieldValue, handleBlur } = useFormik<IShiftForm>({
		validationSchema: getValidationSchema(
			generalSettings?.minShiftLength,
			generalSettings?.maxShiftLength
		),
		enableReinitialize: false,
		initialValues,
		onSubmit: (data: IShiftPayload) => {
			console.log('Submitting shift data:', data);
			// Send dates as-is without any timezone conversion
			const apiPayload: IShiftPayload = {
				workplace: `api/workplaces/${workplaceId}`,
				startAt: moment(data.startAt).format('YYYY-MM-DDTHH:mm:ss'),
				endAt: moment(data.endAt).format('YYYY-MM-DDTHH:mm:ss'),
				employee: data.employee || null,
				jobCode: data.jobCode || null,
				position: data.position || null,
				startOfWeek: weekStartDate ? moment(weekStartDate).format('YYYY-MM-DD') : undefined,
			};

			if (editShift?.id || editShift?.shift_id) {
				// Use shift_id from calendar event or id from editShift
				const id = editShift.shift_id || editShift.id;
				updateShift({
					id,
					body: apiPayload,
					metricDates: collectShiftMetricDates(
						editShift.startAt,
						editShift.start,
						apiPayload.startAt
					),
				});
			} else {
				addShift(apiPayload);
			}
		},
	});

	// Update end time when start time changes to maintain default shift length
	useEffect(() => {
		if (values.startAt && !editShift) {
			// Only auto-update for new shifts, not when editing existing shifts
			const defaultHours = generalSettings?.defaultShiftLengthHours || 8;
			const newEndTime = toLocalDateTimeValue(
				moment(values.startAt).add(defaultHours, 'hours').toDate()
			);
			setFieldValue('endAt', newEndTime);
		}
	}, [values.startAt, editShift, generalSettings?.defaultShiftLengthHours, setFieldValue]);

	const handleSubmitForm = (e: any) => {
		e.preventDefault();
		submitForm();
	};

	const handleDateTimeChange = (field: 'startAt' | 'endAt', date: string, time: string) => {
		// Create a proper datetime string in browser's local timezone
		const dateTimeString = `${date} ${time}:00`;
		const momentDateTime = moment(dateTimeString, 'YYYY-MM-DD HH:mm:ss');

		// Store as ISO string (represents local time)
		setFieldValue(field, momentDateTime.format('YYYY-MM-DDTHH:mm:ss'));
	};

	const handlePickerDateTimeChange = (field: 'startAt' | 'endAt', value: Dayjs | null) => {
		if (!value || !value.isValid()) return;
		handleDateTimeChange(field, value.format('YYYY-MM-DD'), value.format('HH:mm'));
	};

	const dateTimePickerLayoutSx = {
		'& .MuiMultiSectionDigitalClockSection-root': {
			'&::-webkit-scrollbar': {
				width: '4px',
			},
			'&::-webkit-scrollbar-track': {
				background: 'transparent',
			},
			'&::-webkit-scrollbar-thumb': {
				background: '#c0c0c0',
				borderRadius: '2px',
			},
			'&::-webkit-scrollbar-thumb:hover': {
				background: '#999',
			},
			scrollbarWidth: 'thin',
			scrollbarColor: '#c0c0c0 transparent',
		},
	};

	const getDateTimePickerSlotProps = (field: 'startAt' | 'endAt') => ({
		textField: {
			fullWidth: true,
			name: field,
			className: styles.formInput,
			error: touched[field] && Boolean(errors[field]),
			onBlur: handleBlur,
		},
		layout: {
			sx: dateTimePickerLayoutSx,
		},
	});

	const getStartDateLimits = useMemo(
		() =>
			getShiftFormDateLimits({
				weekStartDate,
				isEdit: Boolean(editShift),
				existingStart: editShift?.start || editShift?.startAt,
				existingEnd: editShift?.end || editShift?.endAt,
			}),
		[weekStartDate, editShift]
	);

	// Constant for house shift option
	const HOUSE_SHIFT_OPTION = useMemo(
		() => ({ id: 'HOUSE_SHIFT', firstName: 'House', lastName: 'Shift', '@id': 'null' }),
		[]
	);

	// Build employee options list (includes house shift and sorted employees)
	const employeeOptions = useMemo(
		() =>
			withAssignedEmployeeOption(
				sortedEmployees,
				editShift?.employee || addNewShift?.employeeFromResource,
				HOUSE_SHIFT_OPTION
			),
		[HOUSE_SHIFT_OPTION, sortedEmployees, editShift?.employee, addNewShift?.employeeFromResource]
	);

	const positionOptions = useMemo(() => {
		if (!values.jobCode) return [NONE_POSITION_OPTION];
		const selectedJobCode = sortedJobCodes?.find((jc) => jc['@id'] === values.jobCode);
		return [
			NONE_POSITION_OPTION,
			...filterPositionsByJobCode(positions, values.jobCode, selectedJobCode?.id),
		];
	}, [positions, values.jobCode, sortedJobCodes]);

	const currentPositionValue = useMemo(() => {
		if (!values.position) return NONE_POSITION_OPTION;
		return positionOptions.find((p) => p['@id'] === values.position) || NONE_POSITION_OPTION;
	}, [values.position, positionOptions]);

	useEffect(() => {
		if (!values.position || values.position === NONE_POSITION_OPTION['@id']) return;
		const stillValid = positionOptions.some((option) => option['@id'] === values.position);
		if (!stillValid) {
			setFieldValue('position', null);
		}
	}, [positionOptions, setFieldValue, values.position]);

	// Get current employee value for the Autocomplete
	const currentEmployeeValue = useMemo(() => {
		if (values.employee === null || values.employee === undefined) {
			return HOUSE_SHIFT_OPTION;
		}
		const found = findEmployeeOption(employeeOptions, values.employee);
		if (found) return found;
		return (
			findEmployeeOption(employeeOptions, editShift?.employee) ||
			HOUSE_SHIFT_OPTION
		);
	}, [values.employee, employeeOptions, HOUSE_SHIFT_OPTION, editShift?.employee]);

	if (isLoadingJobCodes) {
		return (
			<Box
				sx={{
					backgroundColor: '#f4f6fa',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					py: 8,
				}}
			>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Paper sx={{ py: 3, borderRadius: 3, boxShadow: 2 }}>
			<Box maxWidth="1100px" mx="auto" px={3}>
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
					<Button
						onClick={onBack}
						sx={{
							backgroundColor: 'rgb(68, 99, 136)',
							color: '#fff',
							textTransform: 'none',
							fontWeight: 500,
							px: 3,
							py: 0.75,
							'&:hover': { backgroundColor: 'rgb(57, 80, 111)' },
						}}
					>
						Back
					</Button>

					<Typography variant="h5" sx={{ fontWeight: 600 }}>
						{editShift ? 'Edit Shift' : 'Create Shift'}
					</Typography>

					<Button
						type="submit"
						variant="contained"
						form="addShiftForm"
						sx={{
							backgroundColor: 'rgb(68, 99, 136)',
							color: '#fff',
							px: 4,
							textTransform: 'none',
							fontWeight: 500,
							'&:hover': { backgroundColor: 'rgb(57, 80, 111)' },
						}}
						disabled={isAddLoading || isUpdateLoading}
					>
						{isAddLoading || isUpdateLoading ? 'Saving...' : 'Save'}
					</Button>
				</Box>

				<Box sx={{ p: 4 }}>
					<Typography variant="h6" gutterBottom>
						Shift Information
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
						Enter shift details and scheduling information
					</Typography>

					<form id="addShiftForm" onSubmit={handleSubmitForm}>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<Grid container spacing={2}>
								{/* Start Date/Time — first so availability can use these values */}
								<Grid item xs={12} md={6}>
									<label className={styles.formLabel}>
										Start Date/Time <span className={styles.required}>*</span>
									</label>
									<DateTimePicker
										value={dayjs(values.startAt).isValid() ? dayjs(values.startAt) : null}
										onChange={(newValue) => handlePickerDateTimeChange('startAt', newValue)}
										views={['year', 'month', 'day', 'hours', 'minutes']}
										minDate={
											getStartDateLimits.minDate
												? dayjs(`${getStartDateLimits.minDate}T00:00:00`)
												: undefined
										}
										maxDate={
											getStartDateLimits.maxDate
												? dayjs(`${getStartDateLimits.maxDate}T23:59:59`)
												: undefined
										}
										sx={{ width: '100%' }}
										slotProps={getDateTimePickerSlotProps('startAt')}
									/>
									{touched.startAt && errors.startAt && (
										<Box sx={{ color: '#d32f2f', fontSize: '0.75rem', mt: 0.5 }}>
											{errors.startAt}
										</Box>
									)}
								</Grid>

								{/* End Date/Time */}
								<Grid item xs={12} md={6}>
									<label className={styles.formLabel}>
										End Date/Time <span className={styles.required}>*</span>
									</label>
									<DateTimePicker
										value={dayjs(values.endAt).isValid() ? dayjs(values.endAt) : null}
										onChange={(newValue) => handlePickerDateTimeChange('endAt', newValue)}
										views={['year', 'month', 'day', 'hours', 'minutes']}
										minDate={
											getStartDateLimits.minDate
												? dayjs(`${getStartDateLimits.minDate}T00:00:00`)
												: undefined
										}
										sx={{ width: '100%' }}
										slotProps={getDateTimePickerSlotProps('endAt')}
									/>
									{touched.endAt && errors.endAt && (
										<Box sx={{ color: '#d32f2f', fontSize: '0.75rem', mt: 0.5 }}>
											{errors.endAt}
										</Box>
									)}
								</Grid>

							{/* Job Code — before Employee so shift timing and role are defined first */}
							<Grid item xs={12} sm={6}>
								<label className={styles.formLabel}>
									Job Code <span className={styles.required}>*</span>
								</label>
								<Autocomplete
									options={sortedJobCodes}
									getOptionLabel={(option) =>
										option.jobNumber ? `${option.jobNumber} - ${option.label}` : option.label
									}
									value={
										values.jobCode
											? sortedJobCodes?.find((jc) => jc['@id'] === values.jobCode) || null
											: null
									}
									onChange={(_, value) => {
										setFieldValue('jobCode', value?.['@id'] || null);
										setFieldValue('position', null);
									}}
									isOptionEqualToValue={(option, value) => option['@id'] === value?.['@id']}
									fullWidth
									renderOption={(props, option) => {
										const { key: _key, ...otherProps } = props;
										return (
											<li key={option['@id'] || option.id} {...otherProps}>
												{option.jobNumber ? `${option.jobNumber} - ${option.label}` : option.label}
											</li>
										);
									}}
									renderInput={(params) => (
										<TextField
											{...params}
											className={styles.formInput}
											error={touched.jobCode && Boolean(errors.jobCode)}
											helperText={touched.jobCode && errors.jobCode}
										/>
									)}
								/>
							</Grid>

							{/* Employee — availability uses the selected start/end date and time */}
							<Grid item xs={12} sm={6}>
								<label className={styles.formLabel}>Employee</label>
								<Autocomplete
									options={employeeOptions}
									getOptionLabel={(option) =>
										option.id === 'HOUSE_SHIFT'
											? 'House Shift'
											: `${option.firstName} ${option.lastName}`
									}
									value={currentEmployeeValue}
									onChange={(_, value) => {
										console.log('Selected employee:', value);
										// If HOUSE_SHIFT is selected, set employee to null; otherwise use @id
										if (value?.['@id'] === 'null') {
											setFieldValue('employee', null);
										} else {
											setFieldValue('employee', toUserIri(value));
										}
									}}
									isOptionEqualToValue={(option, value) => {
										if (!option || !value) return false;
										if (option['@id'] === 'null' && value['@id'] === 'null') return true;
										return Boolean(findEmployeeOption([option], value));
									}}
									fullWidth
									renderOption={(props, option) => {
										const { key: _key, ...otherProps } = props;
										if (option.id === 'HOUSE_SHIFT') {
											return (
												<li key={option['@id'] || option.id} {...otherProps}>
													House Shift
												</li>
											);
										}

										// Calculate employee status from the selected shift date/time
										const shiftStartTime = values.startAt ? new Date(values.startAt) : undefined;
										const shiftEndTime = values.endAt ? new Date(values.endAt) : undefined;
										const status = getEmployeeStatus(option, shiftStartTime, shiftEndTime);

										return (
											<li key={option['@id'] || option.id} {...otherProps}>
												<Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1 }}>
													<Box sx={{ flex: 1 }}>
														<Typography variant="body1">
															{option.firstName} {option.lastName}
														</Typography>
														{status.availabilityMessage && (
															<Typography
																variant="caption"
																sx={{ color: 'warning.main', display: 'block' }}
															>
																{status.availabilityMessage}
															</Typography>
														)}
													</Box>
													<Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
														{status.isUnavailable && (
															<img
																src={unavailableIcon}
																alt="Unavailable"
																title="Employee is unavailable or has time off"
																style={{ width: '18px', height: '18px', objectFit: 'contain' }}
															/>
														)}
														{status.isMinor && (
															<img
																src={minorIcon}
																alt="Minor"
																title="Employee is a minor"
																style={{ width: '18px', height: '18px', objectFit: 'contain' }}
															/>
														)}
														{status.hasOvertime && (
															<img
																src={overtimeIcon}
																alt="Overtime"
																title="Employee is in or approaching overtime"
																style={{ width: '18px', height: '18px', objectFit: 'contain' }}
															/>
														)}
													</Box>
												</Box>
											</li>
										);
									}}
									renderInput={(params) => <TextField {...params} className={styles.formInput} />}
								/>
							</Grid>

							{positions.length > 0 && (
								<Grid item xs={12} sm={6}>
									<label className={styles.formLabel}>Position</label>
									<Autocomplete
										options={positionOptions}
										getOptionLabel={(option) => option.name}
										value={currentPositionValue}
										onChange={(_, value) => {
											setFieldValue(
												'position',
												value?.['@id'] === 'none' ? null : value?.['@id'] || null
											);
										}}
										isOptionEqualToValue={(option, value) => option['@id'] === value?.['@id']}
										disabled={!values.jobCode}
										fullWidth
										renderInput={(params) => (
											<TextField {...params} className={styles.formInput} />
										)}
									/>
								</Grid>
							)}
						</Grid>
						</LocalizationProvider>
					</form>
				</Box>
			</Box>
		</Paper>
	);
};

export default AddShiftPage;
